import { SuitabilityData } from "./SuitabilityForm.schema";
import { PendingOrder, SuitabilityInpData } from "./SuitabilityForm.types";

import { QueryModelPosition, QueryProfile } from "@/api/queries/types";
import { Portfolio } from "@/api/types";

// ---------------------------------------------------------------------------
// Profile map helpers
// ---------------------------------------------------------------------------

type ProfileMap = Record<string, string | number | boolean | null>;

export function buildProfileMap(profileData: QueryProfile[]): ProfileMap {
  const map: ProfileMap = {};
  for (const p of profileData) {
    let value: string | number | boolean | null = null;
    switch (p.primaryType ?? "") {
      case "java.lang.Boolean":
        value = p.booleanValue;
        break;
      case "java.lang.Double":
        value = p.doubleValue;
        break;
      case "java.lang.Integer":
      case "java.lang.Long":
        value = p.intValue;
        break;
      case "java.util.Date":
        value = p.dateValue;
        break;
      default:
        value = p.stringValue ?? null;
    }
    map[p.attributeKey] = value;
  }
  return map;
}

function pStr(map: ProfileMap, key: string): string {
  return String(map[key] ?? "");
}

function pNum(map: ProfileMap, key: string): number {
  return Number(map[key] ?? 0);
}

/** Format a number with two decimals and non-breaking-space thousands-separator. */
function df(value: number): string {
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
}

// ---------------------------------------------------------------------------
// Classification & investor type
// ---------------------------------------------------------------------------

function getClassification(map: ProfileMap): string {
  switch (pStr(map, "contact.profiling.classification")) {
    case "non-professional":
      return "Ikke-profesjonell";
    case "professional":
      return "Profesjonell";
    case "qualified counterparty":
      return "Kvalifisert motpart";
    default:
      return "Ikke definert";
  }
}

function getInvestorType(map: ProfileMap): string {
  let answered = 0;
  let answer = 0;
  const addInt = (key: string) => {
    const n = parseInt(pStr(map, key), 10);
    if (!isNaN(n)) {
      answer += n;
      answered++;
    }
  };
  addInt("contact.profiling.3031appro");
  addInt("contact.profiling.3032appro");
  addInt("contact.profiling.3033appro");
  addInt("contact.profiling.3035appro");
  switch (pStr(map, "contact.profiling.3042appro")) {
    case "Nei, ingen annen en daglig bank":
      answer += 1;
      answered++;
      break;
    case "Kjøp/salg av aksjer/fond på eget initiativ":
      answer += 5;
      answered++;
      break;
    case "Porteføljeforvaltning / Investeringsrådgivning":
      answer += 6;
      answered++;
      break;
  }
  switch (pStr(map, "contact.profiling.3043appro")) {
    case "Grunnskole / videregående skole":
      answer += 1;
      answered++;
      break;
    case "Universitet / høyskole":
      answer += 4;
      answered++;
      break;
    case "Spesialisering i økonomi/finans":
      answer += 6;
      answered++;
      break;
  }
  switch (pStr(map, "contact.profiling.3044appro")) {
    case "Ja":
      answer += 6;
      answered++;
      break;
    case "Nei":
      answer += 4;
      answered++;
      break;
  }
  if (answered < 7) return "ikke definert";
  return answer / answered <= 3.0 ? "Grunnleggende" : "Informert";
}

// ---------------------------------------------------------------------------
// Time horizon
// ---------------------------------------------------------------------------

function getTimeHorizonVal(map: ProfileMap): number {
  const v = pStr(map, "contact.profiling.2051suit");
  return v === "5" ? 5 : parseInt(v, 10) || 1;
}

function getTimeHorizonText(map: ProfileMap): string {
  switch (pStr(map, "contact.profiling.2051suit")) {
    case "1":
      return "Mindre enn 1 år";
    case "2":
      return "1 til 5 år";
    case "3":
      return "Mer enn 5 år";
    case "5":
      return "Mer enn 15 år";
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Risk appetite / return target / investor preference
// ---------------------------------------------------------------------------

function getRiskAppetiteVal(map: ProfileMap): number {
  switch (pStr(map, "contact.profiling.2067suit")) {
    case "1":
      return 0.3;
    case "2":
      return 0.5;
    case "3":
      return 0.6;
    case "4":
      return 0.8;
    case "5":
      return 1.0;
    default:
      return 0.0;
  }
}

function getRiskAppetiteText(map: ProfileMap): string {
  switch (pStr(map, "contact.profiling.2067suit")) {
    case "1":
      return "Lav";
    case "2":
      return "Lav til medium";
    case "3":
      return "Medium";
    case "4":
      return "Medium til høy";
    case "5":
      return "Høy";
    default:
      return "";
  }
}

function getReturnTarget(map: ProfileMap): number {
  switch (pStr(map, "contact.profiling.2066suit")) {
    case "1":
      return 0.3;
    case "2":
      return 0.5;
    case "3":
      return 0.6;
    case "4":
      return 0.8;
    case "5":
      return 1.0;
    default:
      return 0.0;
  }
}

function getInvestorPreference(map: ProfileMap): 1 | 2 | 3 {
  const riskApp = getRiskAppetiteVal(map);
  let avg = (getReturnTarget(map) + riskApp) / 2;
  if (avg < riskApp) avg = riskApp;
  const pct = avg * 100;
  if (pct <= 35) return 1;
  if (pct <= 75) return 2;
  return 3;
}

function getInvestorPreferenceText(map: ProfileMap): string {
  const v = getInvestorPreference(map);
  return v === 1 ? "Lav" : v === 2 ? "Middels" : "Høy";
}

// ---------------------------------------------------------------------------
// Risk tolerance
// ---------------------------------------------------------------------------

function scoreToRiskTolerance(score: number): number {
  if (score <= 1.6) return 30;
  if (score <= 2.3) return 50;
  if (score <= 3.0) return 66;
  if (score <= 3.5) return 80;
  return 100;
}

function getRisktolerance(map: ProfileMap): number {
  const newKeys = [
    "contact.profiling.2052suit",
    "contact.profiling.2053suit",
    "contact.profiling.2054suit",
    "contact.profiling.2055suit",
    "contact.profiling.2056suit",
    "contact.profiling.2057suit",
  ];
  let answered = 0;
  let answer = 0;
  let allPresent = true;
  for (const key of newKeys) {
    const n = parseInt(pStr(map, key), 10);
    if (!isNaN(n)) {
      answer += n;
      answered++;
    } else {
      allPresent = false;
    }
  }
  if (allPresent && answered === 6)
    return scoreToRiskTolerance(answer / answered);

  // Fallback: old questions
  const convMap: Record<string, number> = {
    "5": 1,
    "4": 2,
    "3": 3,
    "2": 4,
    "1": 5,
  };
  const oldKeys = [
    "contact.profiling.2061suit",
    "contact.profiling.2062suit",
    "contact.profiling.2063suit",
    "contact.profiling.2064suit",
    "contact.profiling.2065suit",
  ];
  answered = 0;
  answer = 0;
  for (let i = 0; i < oldKeys.length; i++) {
    const v = pStr(map, oldKeys[i]);
    const n = parseInt(v, 10);
    if (!isNaN(n)) {
      answer += i <= 1 ? convMap[v] ?? n : n;
      answered++;
    }
  }
  if (answered < 5) return 30;
  return scoreToRiskTolerance(answer / answered);
}

// ---------------------------------------------------------------------------
// Stock part thresholds
// ---------------------------------------------------------------------------

function calcStockPartThresholds(map: ProfileMap): {
  maxStockPart: number;
  recMinStockPart: number;
  recMaxStockPart: number;
} {
  const timeHorizon = getTimeHorizonVal(map);
  const investorPref = getInvestorPreference(map);
  const riskTol = getRisktolerance(map) / 100;

  const maxByTime: Record<number, number> = { 1: 0.3, 2: 0.75, 3: 1.0, 5: 1.0 };
  const maxByPref: Record<number, number> = { 1: 0.3, 2: 0.7, 3: 1.0 };
  const maxStockPart = Math.min(
    maxByTime[timeHorizon] ?? 1.0,
    maxByPref[investorPref]
  );

  const recByTime: Record<number, number> = { 1: 0.0, 2: 0.6, 3: 1.0, 5: 1.0 };
  const recByPref: Record<number, number> = { 1: 0.4, 2: 0.8, 3: 1.0 };
  const recMenPart = Math.min(
    recByTime[timeHorizon] ?? 1.0,
    recByPref[investorPref],
    riskTol
  );

  const pct = recMenPart * 100;
  let recMinStockPart: number;
  let recMaxStockPart: number;
  if (pct <= 20) {
    recMinStockPart = 0.0;
    recMaxStockPart = 0.2;
  } else if (pct <= 40) {
    recMinStockPart = 0.2;
    recMaxStockPart = 0.4;
  } else if (pct <= 60) {
    recMinStockPart = 0.4;
    recMaxStockPart = 0.7;
  } else if (pct <= 80) {
    recMinStockPart = 0.6;
    recMaxStockPart = 0.8;
  } else {
    recMinStockPart = 0.75;
    recMaxStockPart = 1.0;
  }

  if (maxStockPart < recMaxStockPart) recMaxStockPart = maxStockPart;
  return { maxStockPart, recMinStockPart, recMaxStockPart };
}

// ---------------------------------------------------------------------------
// Stock part from portfolios
// ---------------------------------------------------------------------------

const NON_STOCK_CLASS_CODES = ["C", "F"];

/**
 * Compute the fraction of a model that is in stock instruments.
 * class_type1 in ["1","12"] means NON-stock (fixed income / other); all other codes are stock.
 * `share_of_portfolio` is normalised by summing all positions.
 */
const NON_STOCK_MODEL_CLASS_CODES = ["1", "12"];

function clampFraction(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

export function getModelStockFraction(
  modelShortName: string,
  modelsData: QueryModelPosition[]
): number {
  const positions = modelsData.filter((p) => p.short_name === modelShortName);
  if (positions.length === 0) return 0;
  const total = positions.reduce((s, p) => s + p.share_of_portfolio, 0);
  if (total === 0) return 0;
  const stockTotal = positions
    .filter(
      (p) => !NON_STOCK_MODEL_CLASS_CODES.includes(String(p.class_type1 ?? ""))
    )
    .reduce((s, p) => s + p.share_of_portfolio, 0);
  return stockTotal / total;
}

export function calcStockPartsFromPortfolios(
  activePorts: Portfolio[],
  pendingOrders: PendingOrder[] = [],
  modelsData: QueryModelPosition[] = []
): { current: number; fin: number } {
  let totalCurrent = 0;
  let stockCurrent = 0;
  let totalFin = 0;
  let stockFin = 0;

  const orderedPortIds = new Set<string>();

  for (const port of activePorts) {
    let portItemTotal = 0;
    let portStock = 0;
    for (const item of port.portfolioReport?.portfolioReportItems ?? []) {
      const mv = item.marketValue ?? 0;
      portItemTotal += mv;
      if (
        !NON_STOCK_CLASS_CODES.includes(item.security?.classType1?.code ?? "")
      ) {
        portStock += mv;
      }
    }
    const portCash = (port.portfolioReport?.marketValue ?? 0) - portItemTotal;
    const portTotal = portItemTotal + portCash;

    totalCurrent += portTotal;
    stockCurrent += portStock;

    // Find orders for this portfolio that have a real model assigned
    const portOrder = pendingOrders.find(
      (o) =>
        String(o.portfolioId) === String(port.id) &&
        o.modelType &&
        o.modelType !== "NOMODEL"
    );

    if (portOrder) {
      // Groovy logic: the entire portfolio is reallocated to the model.
      // sumTradeAmount = existing cash + positions + new money - withdrawals
      orderedPortIds.add(String(port.id));
      const sumTradeAmount =
        (portOrder.sumAvCash ?? 0) +
        (portOrder.sumInsValue ?? 0) +
        (portOrder.amountToInvest ?? 0) -
        (portOrder.withdrawal ?? 0);
      const stockFraction = getModelStockFraction(
        portOrder.modelType ?? "",
        modelsData
      );
      stockFin += sumTradeAmount * stockFraction;
      totalFin += sumTradeAmount;
    } else {
      // No model order — existing holdings carry forward unchanged
      stockFin += portStock;
      totalFin += portTotal;

      // NOMODEL order: new money split by the actual stock fraction from buy orders
      const noModelOrder = pendingOrders.find(
        (o) =>
          String(o.portfolioId) === String(port.id) &&
          (!o.modelType || o.modelType === "NOMODEL")
      );
      if (noModelOrder) {
        const net =
          (noModelOrder.amountToInvest ?? 0) - (noModelOrder.withdrawal ?? 0);
        const sf = noModelOrder.stockFraction ?? 0;
        totalFin += net;
        if (net > 0) {
          stockFin += net * sf;
        } else if (net < 0 && portTotal > 0) {
          // net withdrawal — reduce stock proportionally to existing holdings
          stockFin += net * (portStock / portTotal);
        }
      }
    }
  }

  // Orders for new portfolios (no matching active portfolio) — add them to fin only.
  const activePortIds = new Set(activePorts.map((p) => String(p.id)));
  for (const o of pendingOrders) {
    if (activePortIds.has(String(o.portfolioId))) continue;
    if (o.modelType && o.modelType !== "NOMODEL") {
      // Model-based order: split by model's stock fraction
      const sumTradeAmount =
        (o.sumAvCash ?? 0) +
        (o.sumInsValue ?? 0) +
        (o.amountToInvest ?? 0) -
        (o.withdrawal ?? 0);
      const stockFraction = getModelStockFraction(o.modelType, modelsData);
      stockFin += sumTradeAmount * stockFraction;
      totalFin += sumTradeAmount;
    } else if (!o.modelType || o.modelType === "NOMODEL") {
      // NOMODEL order for a new portfolio: use stock fraction from buy orders
      const net = (o.amountToInvest ?? 0) - (o.withdrawal ?? 0);
      const sf = o.stockFraction ?? 0;
      totalFin += net;
      stockFin += net * sf;
    }
  }

  if (totalCurrent === 0 && totalFin === 0) return { current: 0, fin: 0 };
  return {
    current:
      totalCurrent === 0 ? 0 : clampFraction(stockCurrent / totalCurrent),
    fin: totalFin === 0 ? 0 : clampFraction(stockFin / totalFin),
  };
}

// ---------------------------------------------------------------------------
// System advice text
// ---------------------------------------------------------------------------

function buildSystemAdvice(map: ProfileMap, finStockPart: number): string {
  const { maxStockPart, recMinStockPart, recMaxStockPart } =
    calcStockPartThresholds(map);
  const fin = Math.min(finStockPart, 1.0);
  const r = (v: number) => Math.round(v * 100) / 100;

  const inTarget = r(fin) >= r(recMinStockPart) && r(fin) <= r(recMaxStockPart);
  const aboveButWithinMax =
    r(fin) > r(recMaxStockPart) && r(fin) <= r(maxStockPart);
  const belowTarget = r(fin) < r(recMinStockPart);

  const text2 = inTarget
    ? "Den samlede porteføljen etter gjennomføring er innenfor de investeringsrammer vi har anbefalt som følge av den samlede informasjon vi har om kundeforholdet."
    : "Den samlede porteføljen etter gjennomføring er ikke innenfor de investeringsrammer vi har anbefalt som følge av den samlede informasjon vi har om kundeforholdet.";

  let text3 = "";
  if (fin > maxStockPart) {
    text3 =
      " Porteføljens risiko i form av aksjeandel, etter gjennomført allokering, er høyere enn det vi anser som tilrådelig basert på angitt risikopreferanse og investeringsmål.»";
  } else if (aboveButWithinMax) {
    text3 =
      " Porteføljens risiko i form av aksjeandel, etter gjennomført allokering, er høyere enn det vi anser som tilrådelig basert på angitt risikopreferanse og investeringsmål, men innenfor det som kan anses som høyeste forsvarlige risikonivå.";
  } else if (belowTarget) {
    text3 =
      " Porteføljens risiko i form av aksjeandel, etter gjennomført allokering, er lavere enn det vi normalt vil anbefale basert på angitt risikopreferanse og investeringsmål.";
  }
  return (text2 + text3).trim();
}

// ---------------------------------------------------------------------------
// Financial situation (juridical)
// ---------------------------------------------------------------------------

function buildFinancialSituation(map: ProfileMap): string {
  const balance = pNum(map, "contact.profiling.2032suitOrg");
  const equity = pNum(map, "contact.profiling.2033suitOrg");

  if (!balance && !equity) return "";
  const sumInvest =
    pNum(map, "contact.profiling.2034suitOrg") +
    pNum(map, "contact.profiling.2030suitOrg") +
    pNum(map, "contact.profiling.2035suitOrg");
  return `Balanse: ${df(balance)} hvorav EK: ${df(
    equity
  )}. Sum investeringer og cash: ${df(sumInvest)}`;
}

// ---------------------------------------------------------------------------
// ESG preference
// ---------------------------------------------------------------------------

function getESGPreference(map: ProfileMap): string {
  const v = pStr(map, "contact.profiling.2071suit");
  return v === "Ja" || v === "Nei" ? v : "Ikke definert";
}

// ---------------------------------------------------------------------------
// Main exported computation
// ---------------------------------------------------------------------------

export function computeSuitabilityInpData(
  profileData: QueryProfile[],
  activePorts: Portfolio[] = [],
  pendingOrders: PendingOrder[] = [],
  modelsData: QueryModelPosition[] = []
): SuitabilityInpData {
  const map = buildProfileMap(profileData);
  const { current, fin } = calcStockPartsFromPortfolios(
    activePorts,
    pendingOrders,
    modelsData
  );
  const { maxStockPart, recMinStockPart, recMaxStockPart } =
    calcStockPartThresholds(map);

  return {
    knowledgeAndExperience: `${getInvestorType(map)}, ${getClassification(
      map
    )}`,
    financialSituation: buildFinancialSituation(map),
    timeHorizon: getTimeHorizonText(map),
    riskTolerance: `Maks aksjeandel: ${df(maxStockPart * 100)} %, Anb fra: ${df(
      recMinStockPart * 100
    )} % til ${df(recMaxStockPart * 100)} %`,
    riskpreferenceAndInvestmentGoals: `Risikopreferanse ${getRiskAppetiteText(
      map
    )}, Tidshorisont: ${getTimeHorizonText(
      map
    )}, Systemvurdering: ${getInvestorPreferenceText(map)}`,
    esgPreference: getESGPreference(map),
    stockpartBefore: df(current * 100),
    stockpartAfter: df(fin * 100),
    systemAdvice: buildSystemAdvice(map, fin),
  };
}

/**
 * Flatten SuitabilityData into the Form.io key naming convention.
 * All field keys already match Form.io convention.
 */
export function flattenSuitability(
  data: SuitabilityData
): Record<string, unknown> {
  return { ...data };
}

// ---------------------------------------------------------------------------
// Raw suitability values for document production
// ---------------------------------------------------------------------------

/**
 * Returns the individual computed suitability values needed for document
 * template parameter population (used by buildDocumentProductionList).
 */
export function computeSuitabilityDocParams(
  profileData: QueryProfile[],
  activePorts: Portfolio[],
  pendingOrders: PendingOrder[],
  modelsData: QueryModelPosition[]
) {
  const map = buildProfileMap(profileData);
  const { current, fin } = calcStockPartsFromPortfolios(
    activePorts,
    pendingOrders,
    modelsData
  );
  const { maxStockPart, recMinStockPart, recMaxStockPart } =
    calcStockPartThresholds(map);

  return {
    investorType: getInvestorType(map),
    classification: getClassification(map),
    timeHorizonText: getTimeHorizonText(map),
    riskAppetiteText: getRiskAppetiteText(map),
    esgPreference: getESGPreference(map),
    maxStockPart,
    recMinStockPart,
    recMaxStockPart,
    currentStockPart: current,
    finStockPart: fin,
    systemAdvice: buildSystemAdvice(map, fin),
    // Org financial data
    balance: pNum(map, "contact.profiling.2032suitOrg"),
    equity: pNum(map, "contact.profiling.2033suitOrg"),
    portfolioStock: pNum(map, "contact.profiling.2034suitOrg"),
    portfolioFixed: pNum(map, "contact.profiling.2030suitOrg"),
    portfolioCash: pNum(map, "contact.profiling.2035suitOrg"),
    // Private financial data
    fortune: pNum(map, "contact.profiling.2039suitPrivate"),
    realEstate: pNum(map, "contact.profiling.2036suitPrivate"),
    debt: pNum(map, "contact.profiling.2038suitPrivate"),
  };
}
